import React, { useCallback, useEffect, useState } from "react";
import { Button, Container, Header, Message, Modal } from "semantic-ui-react";

import * as MessageList from "components/MessageList";
import * as AccountService from "services/accounts";
import * as AccountsTable from "components/Admin/Accounts/AccountsTable";
import * as AccountsTableColumns from "components/Admin/Accounts/AccountsTableColumns";

const PendingRequests = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [messages, sendMessage] = MessageList.useMessages();

  const refreshAccounts = () =>
    AccountService.fetchPendingRequestAccounts().then((accounts) =>
      setAccounts(accounts)
    );

  // Initial load
  useEffect(() => {
    refreshAccounts();
    setLoading(false);
  }, []);

  const onSelectAccount = useCallback(
    (account) => setSelectedAccounts([...selectedAccounts, account]),
    [selectedAccounts, setSelectedAccounts]
  );

  const onConfirmApprove = useCallback(async () => {
    setLoading(true);
    try {
      await AccountService.approveAccountRequestsByUserId(selectedAccounts);
      sendMessage((dismiss) => (
        <ApproveSuccessMessage accounts={selectedAccounts} dismiss={dismiss} />
      ));
      await refreshAccounts();
    } catch (error) {
      sendMessage((dismiss) => (
        <ApproveFailureMessage
          accounts={selectedAccounts}
          dismiss={dismiss}
          errorMessage={error.message}
        />
      ));
    } finally {
      setLoading(false);
    }
  }, [sendMessage, selectedAccounts]);

  const onConfirmDeny = useCallback(async () => {
    setLoading(true);
    setDenyModalOpen(false);
    try {
      await AccountService.denyAccountRequestsByUserId(selectedAccounts);
      sendMessage((dismiss) => (
        <DenySuccessMessage accounts={selectedAccounts} dismiss={dismiss} />
      ));
      await refreshAccounts();
    } catch (error) {
      sendMessage((dismiss) => (
        <DenyFailureMessage
          accounts={selectedAccounts}
          dismiss={dismiss}
          errorMessage={error.message}
        />
      ));
    } finally {
      setLoading(false);
    }
  }, [sendMessage, selectedAccounts]);

  return (
    <Container fluid style={{ padding: "2em" }}>
      <Header as="h1">Pending requests</Header>
      <MessageList.MessageList messages={messages} />
      <AccountsTable.AccountsTable
        accounts={accounts}
        columns={[
          AccountsTableColumns.EmailAddress,
          AccountsTableColumns.DateRequested,
          AccountsTableColumns.RequestedPage,
        ]}
        loading={loading}
        selectedAccounts={selectedAccounts}
        setSelectedAccounts={setSelectedAccounts}
        onSelectAccount={onSelectAccount}
      >
        <TableActions
          canApprove={!loading && selectedAccounts.length > 0}
          onClickApprove={onConfirmApprove}
          canDeny={!loading && selectedAccounts.length > 0}
          onClickDeny={() => setDenyModalOpen(true)}
          refreshAccounts={refreshAccounts}
        />
      </AccountsTable.AccountsTable>
      <DenyAccountModal
        accounts={selectedAccounts}
        onConfirm={onConfirmDeny}
        open={denyModalOpen}
        onClose={() => setDenyModalOpen(false)}
      />
    </Container>
  );
};
export default PendingRequests;

const TableActions = React.memo(
  ({ canApprove, onClickApprove, canDeny, onClickDeny, refreshAccounts }) => (
    <Button.Group>
      <Button
        content="Approve"
        disabled={!canApprove}
        onClick={onClickApprove}
      />
      <Button content="Deny" disabled={!canDeny} onClick={onClickDeny} />
      <Button icon="refresh" role="refresh" onClick={refreshAccounts} />
    </Button.Group>
  )
);

const DenyAccountModal = ({ accounts, onConfirm, open, onClose }) => {
  return (
    accounts && (
      <Modal size="small" open={open} onClose={onClose}>
        <Modal.Header>Confirm request denial</Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to deny this account request? The request will
            be permanently deleted.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Cancel</Button>
          <Button negative onClick={onConfirm}>
            Deny
          </Button>
        </Modal.Actions>
      </Modal>
    )
  );
};

const ApproveSuccessMessage = React.memo(({ accounts, dismiss }) => (
  <Message onDismiss={dismiss} positive>
    <Message.Content>
      Approved account request for{" "}
      {accounts.map((account, index) => (
        <strong>{(index ? ", " : "") + account.EmailAddress}</strong>
      ))}
      .
    </Message.Content>
  </Message>
));

const ApproveFailureMessage = React.memo(
  ({ accounts, errorMessage, dismiss }) => (
    <Message onDismiss={dismiss} negative>
      <Message.Content>
        <p>
          Failed to approve account request for{" "}
          {accounts.map((account, index) => (
            <strong>{(index ? ", " : "") + account.EmailAddress}</strong>
          ))}
        </p>
        {errorMessage && <p>Error message: {errorMessage}</p>}
      </Message.Content>
    </Message>
  )
);

const DenySuccessMessage = React.memo(({ accounts, dismiss }) => (
  <Message onDismiss={dismiss} positive>
    <Message.Content>
      Denied account request for{" "}
      {accounts.map((account, index) => (
        <strong>{(index ? ", " : "") + account.EmailAddress}</strong>
      ))}
      .
    </Message.Content>
  </Message>
));

const DenyFailureMessage = React.memo(({ accounts, errorMessage, dismiss }) => (
  <Message onDismiss={dismiss} negative>
    <Message.Content>
      <p>
        Failed to deny account request for{" "}
        {accounts.map((account, index) => (
          <strong>{(index ? ", " : "") + account.EmailAddress}</strong>
        ))}
        .
      </p>
      {errorMessage && <p>Error message: {errorMessage}</p>}
    </Message.Content>
  </Message>
));
